import { useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, Paperclip } from 'lucide-react';
import { Button } from '@/shared/ui';
import type { AssistenteProposta } from './assistenteService';
import { dataBr, type DiaAbertoMia, type ResultadoPropostaMia } from './useConversaMia';
import { CamposPropostaMia } from './CamposPropostaMia';
import styles from './PropostaMia.module.css';

export type EstadoProposta = 'pendente' | 'confirmada' | 'cancelada';

type Props = {
  proposta: AssistenteProposta;
  estado: EstadoProposta;
  /** Depois de confirmar: link do que foi gravado. */
  resultado?: ResultadoPropostaMia | null;
  /** campos: so o que mudou. Devolve false se a API recusou. */
  onConfirmar: (campos?: Record<string, string>) => Promise<boolean | void>;
  onCancelar: () => Promise<void>;
  /** Acesso recusado por dia anterior sem saida: o cartao oferece fechar aquele dia. */
  diaAberto?: DiaAbertoMia | null;
  onFecharDiaAberto?: () => Promise<void>;
};

function valoresIniciais(proposta: AssistenteProposta): Record<string, string> {
  return Object.fromEntries((proposta.campos ?? []).map((campo) => [campo.chave, campo.valor]));
}

/**
 * Cartao da proposta dentro do balao da Mia que a fez: o que vai ser gravado e os botoes.
 * Editar abre os campos que a API mandou; o Confirmar envia so o que mudou. Os dados vem
 * da proposta, nao do texto da Mia. So o Confirmar grava.
 */
export function PropostaMia({
  proposta,
  estado,
  resultado,
  onConfirmar,
  onCancelar,
  diaAberto,
  onFecharDiaAberto,
}: Props) {
  const [enviando, setEnviando] = useState<'confirmar' | 'cancelar' | null>(null);
  const [editando, setEditando] = useState(false);
  const [valores, setValores] = useState(() => valoresIniciais(proposta));
  const recado = proposta.ferramenta === 'propor_recado';
  const editavel = (proposta.campos?.length ?? 0) > 0;
  const titulo = recado ? 'Recado' : proposta.titulo;

  function alterados(): Record<string, string> {
    const iniciais = valoresIniciais(proposta);
    return Object.fromEntries(Object.entries(valores).filter(([chave, valor]) => valor !== iniciais[chave]));
  }

  async function agir(qual: 'confirmar' | 'cancelar') {
    if (enviando) return;
    setEnviando(qual);
    try {
      if (qual === 'confirmar') {
        await onConfirmar(editando ? alterados() : undefined);
      } else {
        await onCancelar();
      }
    } finally {
      setEnviando(null);
    }
  }

  return (
    <section
      className={`${styles.cartao} ${estado === 'confirmada' ? styles.confirmada : ''} ${
        estado === 'cancelada' ? styles.cancelada : ''
      }`}
      aria-label={recado ? 'Proposta de recado' : `Proposta de ${proposta.titulo}`}
    >
      <p className={styles.tipo}>
        {titulo}
        <span className={styles.para}>Para {proposta.nomeAluno}</span>
      </p>
      {editando && estado === 'pendente' && proposta.campos ? (
        <CamposPropostaMia
          campos={proposta.campos}
          valores={valores}
          desabilitado={enviando !== null}
          onChange={(chave, valor) => setValores((atual) => ({ ...atual, [chave]: valor }))}
        />
      ) : recado ? (
        <dl className={styles.campos}>
          <div>
            <dt>Título</dt>
            <dd>{proposta.titulo}</dd>
          </div>
          <div>
            <dt>Recado</dt>
            <dd>{proposta.texto}</dd>
          </div>
        </dl>
      ) : (
        <p className={styles.texto}>{proposta.texto}</p>
      )}
      {proposta.anexos?.length ? (
        <p className={styles.comAnexo}>
          <Paperclip size={14} aria-hidden />
          {proposta.anexos.join(', ')}
        </p>
      ) : null}
      {estado === 'pendente' && diaAberto ? (
        <div className={styles.diaAberto} role="alert">
          <p>{diaAberto.mensagem}</p>
          {onFecharDiaAberto ? (
            <Button
              size="sm"
              variant="secondary"
              disabled={enviando !== null}
              onClick={() => void onFecharDiaAberto()}
            >
              Registrar a saída de {dataBr(diaAberto.data).slice(0, 5)}
            </Button>
          ) : null}
        </div>
      ) : null}
      {estado === 'pendente' ? (
        <div className={styles.acoes}>
          <Button
            size="sm"
            isLoading={enviando === 'confirmar'}
            disabled={enviando !== null}
            onClick={() => void agir('confirmar')}
          >
            Confirmar
          </Button>
          {editavel && !editando ? (
            <Button size="sm" variant="secondary" disabled={enviando !== null} onClick={() => setEditando(true)}>
              Editar
            </Button>
          ) : null}
          <Button
            size="sm"
            variant="outline"
            disabled={enviando !== null}
            onClick={() => void agir('cancelar')}
          >
            Cancelar
          </Button>
        </div>
      ) : null}
      {estado === 'confirmada' ? (
        <>
          <p className={styles.resultado}>
            <CheckCircle2 size={16} aria-hidden />
            {resultado?.caminho ? (
              <Link to={resultado.caminho}>{resultado.rotulo}</Link>
            ) : (
              (resultado?.rotulo ?? 'Registro gravado.')
            )}
          </p>
          {resultado?.detalhes.length ? (
            <ul className={styles.detalhes} aria-label="Não gravados">
              {resultado.detalhes.map((detalhe) => (
                <li key={detalhe}>
                  <AlertTriangle size={14} aria-hidden />
                  {detalhe}
                </li>
              ))}
            </ul>
          ) : null}
        </>
      ) : null}
      {estado === 'cancelada' ? <p className={styles.resultado}>Proposta cancelada.</p> : null}
    </section>
  );
}
